import { RefObject } from 'react'
import { create } from 'zustand'

import { calculateFileSha256 } from '../core'
import { type UploadProgressStep } from '../utils'

export type FileUploaderEntryFile = {
  id: string
  file: File
  hash: string
}

export type FileUploaderProcessedFile = {
  id: string
  url: string
  name?: string
  contentType?: string
}

type FileUploaderStatus = {
  status: 'unstarted' | 'uploading' | 'done' | 'error'
  step?: UploadProgressStep
  phase?: 'start' | 'end'
  error?: string
  friendlyError?: string
  processedFile?: FileUploaderProcessedFile
}

export type FileUploaderStore = {
  inputRef: RefObject<HTMLInputElement> | undefined
  maxFiles: number
  entryFiles: FileUploaderEntryFile[]
  statuses: Record<string, FileUploaderStatus>
  limitReached: boolean
  loading: boolean
  isDragging: boolean
  getProcessedFiles: () => FileUploaderProcessedFile[]
  openFileDialog: () => void
  prepareFilesToUpload: (files: File[]) => Promise<FileUploaderEntryFile[]>
  setFiles: (files: FileUploaderEntryFile[]) => void
  setStatus: (id: string, status: FileUploaderStatus) => void
  getStatus: (id: string) => FileUploaderStatus | undefined
  clearInput: () => void
  removeFile: (file: FileUploaderEntryFile) => void
  reset: () => void
  register: (ref: RefObject<HTMLInputElement>) => void
  isDone: () => boolean
  setIsDragging: (isDragging: boolean) => void
}

interface HashedFile {
  hash: string
  file: File
}

function areFilesEqual(fileA: HashedFile, fileB: HashedFile) {
  return (
    fileA.file.size === fileB.file.size &&
    (fileA.file.name === fileB.file.name || fileA.hash === fileB.hash)
  )
}

export type CreateFileUploaderStoreOptions = {
  maxFiles?: number
}

export function createFileUploaderStore(options: CreateFileUploaderStoreOptions = {}) {
  const { maxFiles: initialMaxFiles = 10 } = options

  return create<FileUploaderStore>((set, get) => ({
    inputRef: undefined,
    entryFiles: [],
    maxFiles: initialMaxFiles,
    limitReached: false,
    statuses: {},
    loading: false,
    isDragging: false,
    isDone: () => {
      const { entryFiles, statuses } = get()
      return entryFiles.every((entry) => statuses[entry.id]?.status === 'done')
    },
    getProcessedFiles: () => {
      const { entryFiles, statuses } = get()
      return entryFiles
        .map((entry) => statuses[entry.id]?.processedFile)
        .filter((file) => file !== undefined)
    },
    setStatus(id, status) {
      const { statuses } = get()
      const nextStatuses = { ...statuses, [id]: status }
      const nextLoading = Object.values(nextStatuses).some(
        (s) => s.status !== 'done' && s.status !== 'error'
      )
      set({ statuses: nextStatuses, loading: nextLoading })
    },
    getStatus(id) {
      const { statuses } = get()
      return statuses[id]
    },
    async prepareFilesToUpload(newFiles) {
      const { entryFiles, getStatus } = get()
      const newHashedFiles: HashedFile[] = await Promise.all(
        newFiles.map(async (file) => ({ hash: await calculateFileSha256(file), file }))
      )
      return newHashedFiles
        .filter(
          (newHashedFile, index, self) =>
            index ===
            self.findIndex((otherNewHashedFile) => areFilesEqual(newHashedFile, otherNewHashedFile))
        )
        .map((file) => {
          const existingEntryFile = entryFiles.find((entryFile) => areFilesEqual(file, entryFile))
          return (
            existingEntryFile || ({ id: crypto.randomUUID(), ...file } as FileUploaderEntryFile)
          )
        })
        .filter((entryFile) => {
          const status = getStatus(entryFile.id)
          if (!status || status.status === 'unstarted') {
            return true
          }
          return false
        })
    },
    setFiles(entryFiles) {
      const { maxFiles, entryFiles: currentEntryFiles, setStatus } = get()

      const totalFiles = currentEntryFiles.length + entryFiles.length
      if (totalFiles > maxFiles) {
        throw new FileUploaderStoreError('LIMIT_REACHED', undefined, {
          maxFiles,
          attempted: totalFiles,
        })
      }

      const combinedEntryFiles: FileUploaderEntryFile[] = [...currentEntryFiles]
      for (const file of entryFiles) {
        const existingFile = combinedEntryFiles.find((f) => f.id === file.id)
        if (!existingFile) {
          combinedEntryFiles.push(file)
          setStatus(file.id, { status: 'unstarted' })
        }
      }

      const limitReached = combinedEntryFiles.length >= maxFiles
      set({ entryFiles: combinedEntryFiles, limitReached })
    },
    removeFile(file) {
      const { entryFiles, maxFiles } = get()
      const filteredEntryFiles = entryFiles.filter((f) => f.id !== file.id)
      const statuses = { ...get().statuses }
      delete statuses[file.id]
      const limitReached = filteredEntryFiles.length >= maxFiles
      set({ entryFiles: filteredEntryFiles, limitReached, statuses })
    },
    clearInput: () => {
      const { inputRef } = get()
      if (inputRef?.current) {
        inputRef.current.value = ''
      }
    },
    reset: () => {
      get().clearInput()
      set({
        entryFiles: [],
        limitReached: false,
        statuses: {},
        loading: false,
      })
    },
    openFileDialog: () => {
      const { inputRef } = get()
      if (!inputRef?.current) {
        throw new Error('The register method must be called before openFileDialog')
      }
      inputRef.current.click()
    },
    register: (ref) => set({ inputRef: ref }),
    setIsDragging: (isDragging) => set({ isDragging }),
  }))
}

export const useFileUploaderStore = createFileUploaderStore()

export type FileUploaderStoreErrorType = 'LIMIT_REACHED'

export type FileUploaderStoreErrorPayload = {
  maxFiles: number
  attempted: number
}

export class FileUploaderStoreError extends Error {
  errorType: FileUploaderStoreErrorType
  payload?: FileUploaderStoreErrorPayload

  constructor(
    errorType: FileUploaderStoreErrorType,
    message?: string,
    payload?: FileUploaderStoreErrorPayload
  ) {
    super(message)
    this.name = 'FileUploaderStoreError'
    this.errorType = errorType
    this.payload = payload
  }
}
